<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" :disabled="saving" @click="handleSave">
          <span v-if="saving">儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchQuotaSettings">重試</button>
    </div>

    <div v-else class="quota-settings">
      <form @submit.prevent="handleSave">
        <div class="form-section">
          <h2>配額限制</h2>
          
          <div class="form-group">
            <label>每月成本上限 (USD) <span class="required">*</span></label>
            <input
              v-model.number="formData.monthly_cost_limit_usd"
              type="number"
              step="0.01"
              min="0"
              class="form-control"
              required
            />
            <small class="form-hint">設定每月 AI 服務成本上限</small>
          </div>

          <div class="form-group">
            <label>每日成本上限 (USD) <span class="required">*</span></label>
            <input
              v-model.number="formData.daily_cost_limit_usd"
              type="number"
              step="0.01"
              min="0"
              class="form-control"
              required
            />
            <small class="form-hint">設定每日 AI 服務成本上限</small>
          </div>

          <div class="form-group">
            <label>每日請求數上限 <span class="required">*</span></label>
            <input
              v-model.number="formData.request_limit_per_day"
              type="number"
              min="1"
              class="form-control"
              required
            />
            <small class="form-hint">設定每日 API 請求數上限</small>
          </div>
        </div>

        <div class="form-section">
          <h2>當前使用量</h2>
          <div class="usage-stats">
            <div class="stat-item">
              <span class="stat-label">本月成本：</span>
              <span class="stat-value">${{ currentMonthCost }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">今日成本：</span>
              <span class="stat-value">${{ currentDayCost }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">本月請求數：</span>
              <span class="stat-value">{{ quotaSettings?.current_month_requests || 0 }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">今日請求數：</span>
              <span class="stat-value">{{ quotaSettings?.current_day_requests || 0 }}</span>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h2>告警設定</h2>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="formData.notification_enabled"
                type="checkbox"
              />
              <span>啟用通知</span>
            </label>
          </div>

          <div v-if="formData.notification_enabled" class="form-group">
            <label>通知 Email</label>
            <input
              v-model="formData.notification_email"
              type="email"
              class="form-control"
              placeholder="admin@example.com"
            />
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="formData.notification_threshold_90"
                type="checkbox"
              />
              <span>在 90% 時發送告警</span>
            </label>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="formData.notification_threshold_100"
                type="checkbox"
              />
              <span>在 100% 時發送告警</span>
            </label>
          </div>
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ChatMateQuota',
  setup() {
    const { pageTitle } = usePageTitle('配額設定');
    const quotaSettings = ref(null);
    const loading = ref(false);
    const saving = ref(false);
    const error = ref(null);
    const formData = reactive({
      monthly_cost_limit_usd: 100.00,
      daily_cost_limit_usd: 10.00,
      request_limit_per_day: 1000,
      notification_enabled: true,
      notification_email: '',
      notification_threshold_90: true,
      notification_threshold_100: true
    });

    const fetchQuotaSettings = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await chatmateService.getQuotaSettings();

        if (response && response.success && response.data) {
          quotaSettings.value = response.data;
          Object.assign(formData, {
            monthly_cost_limit_usd: response.data.monthly_cost_limit_usd || 100.00,
            daily_cost_limit_usd: response.data.daily_cost_limit_usd || 10.00,
            request_limit_per_day: response.data.request_limit_per_day || 1000,
            notification_enabled: response.data.notification_enabled !== false,
            notification_email: response.data.notification_email || '',
            notification_threshold_90: response.data.notification_threshold_90 !== false,
            notification_threshold_100: response.data.notification_threshold_100 !== false
          });
        } else {
          error.value = response?.message || '獲取配額設定失敗';
        }
      } catch (err) {
        console.error('獲取配額設定失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取配額設定失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    // 格式化成本顯示
    const formatCost = (value) => {
      if (value == null || value === undefined || value === '') {
        return '0.000000';
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return '0.000000';
      }
      return num.toFixed(6);
    };

    // 計算本月成本顯示
    const currentMonthCost = computed(() => {
      return formatCost(quotaSettings.value?.current_month_cost_usd);
    });

    // 計算今日成本顯示
    const currentDayCost = computed(() => {
      return formatCost(quotaSettings.value?.current_day_cost_usd);
    });

    const handleSave = async () => {
      saving.value = true;

      try {
        const response = await chatmateService.updateQuotaSettings(formData);

        if (response && response.success) {
          await showSuccess('儲存成功', '配額設定已更新');
          await fetchQuotaSettings();
        } else {
          await showError('儲存失敗', response?.message || '更新配額設定失敗');
        }
      } catch (err) {
        console.error('更新配額設定失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '更新配額設定失敗';
        await showError('儲存失敗', errorMessage);
      } finally {
        saving.value = false;
      }
    };

    onMounted(() => {
      fetchQuotaSettings();
    });

    return {
      pageTitle,
      quotaSettings,
      loading,
      saving,
      error,
      formData,
      fetchQuotaSettings,
      handleSave,
      currentMonthCost,
      currentDayCost
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.quota-settings {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
}

.form-section:last-child {
  border-bottom: none;
}

.form-section h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

.required {
  color: #e74c3c;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 14px;
  color: #999;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

.usage-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.stat-item {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.stat-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.form-actions {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}
</style>
